// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../api/axios";

const AuthContext = createContext();

function loadFromStorage() {
  const token = localStorage.getItem("accessToken");
  let user = null;

  const raw = localStorage.getItem("authUser");
  if (raw && raw !== "undefined") {
    try {
      user = JSON.parse(raw);
    } catch (err) {
      console.warn("Corrupted authUser in localStorage, clearing it out");
      localStorage.removeItem("authUser");
    }
  }

  return { token, user };
}

export function AuthProvider({ children }) {
  const [{ token, user }, setState] = useState(loadFromStorage);
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data: refreshData } = await api.post("/auth/refresh");
        localStorage.setItem("accessToken", refreshData.accessToken);
        api.defaults.headers.Authorization = `Bearer ${refreshData.accessToken}`;

        const { data: meData } = await api.get("/auth/me");
        setState({
          token: refreshData.accessToken,
          user: meData.user,
        });
        localStorage.setItem("authUser", JSON.stringify(meData.user));
      } catch {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("authUser");
        setState({ token: null, user: null });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setAuth = ({ user, token, remember }) => {
    api.defaults.headers.Authorization = `Bearer ${token}`;
    setState({ user, token });
    if (remember) {
      localStorage.setItem("accessToken", token);
      localStorage.setItem("authUser", JSON.stringify(user));
    }
  };

  const clearAuth = () => {
    api.post("/auth/logout").catch(() => {});
    delete api.defaults.headers.Authorization;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("authUser");
    setState({ user: null, token: null });
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, setAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
